// Libraries
import clsx from "clsx";
import { Grid, Stack, Typography } from "@mui/material";

// Components
import InputSection from "@/Settings/components/PayrollMapping/InputSection/InputSection";

// Types
import { IPayrollProviderCustomFieldOption, ISystemSettingsCustomField } from "@/Settings/types/PayrollMapping/CustomFieldItemsMapping/api";
import { TSelectedValuesInput } from "@/Layouts/types/HierarchicalSelector";

// Layouts
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";

// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { AddCircleRounded } from "@mui/icons-material";

// Helpers
import { isLastInputFulfilled } from "@/Settings/helpers/PayrollMapping";

type TCustomFieldItemRowProps = {
	customFieldItemIndex: number;
	name: string;
	value: ISystemSettingsCustomField[];
	options: IPayrollProviderCustomFieldOption[];
	disabled: boolean;
	onAddNewInput: (customFieldItemIndex: number) => void;
	onRemoveInput: (customFieldItemIndex: number, childIndex: number) => void;
	onChangeInput: (customFieldItemIndex: number, childIndex: number, value?: TSelectedValuesInput) => void;
};

export default function CustomFieldItemRow(props: Readonly<TCustomFieldItemRowProps>) {
	const { customFieldItemIndex, name, value: inputs, options, disabled, onAddNewInput, onRemoveInput, onChangeInput } = props;

	const disabledAddButton = !options?.length || disabled || !isLastInputFulfilled(inputs);
	return (
		<Grid container direction="row" alignItems="flex-start">
			<Grid item xs={2.5}>
				<Typography className="!font-sans !text-[14.5px] !font-normal !text-default">{name}</Typography>
			</Grid>
			<Grid item container direction="row" xs={9.5} alignItems="center">
				{inputs?.map((value, index, array) => {
					const { displayId, fieldName, companyId, fieldId } = value;
					const inputValues = [
						{ label: displayId, value: displayId, extra: companyId },
						{ label: fieldName, value: fieldName, extra: fieldId },
					];

					const ownError = !displayId || !fieldName;
					const anyErrorInSiblings = array.some((input) => !input.displayId || !input.fieldName);

					return (
						<InputSection
							key={`${customFieldItemIndex}-input-${value.fieldId}`}
							inputValues={inputValues}
							onRemoveInput={() => onRemoveInput(customFieldItemIndex, index)}
							options={options.map((opt) => ({
								displayId: opt.displayId,
								companyId: opt.companyId,
								items: opt.customFields,
							}))}
							mapFunction={(item) => ({ name: item.fieldName, id: item.fieldId })}
							disabled={disabled || (!ownError && anyErrorInSiblings)}
							index={{
								itemIndex: customFieldItemIndex,
								inputIndex: index,
							}}
							onChange={onChangeInput}
						/>
					);
				})}
				{/* Add button */}
				<Stack className={clsx({ "!ml-[-8px]": inputs?.length % 4 === 0 })}>
					<SimpleIconButton
						className={clsx({ "!text-primary": !disabledAddButton, "!text-disabled": disabledAddButton })}
						icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
						iconHover={<AddCircleRounded fontSize="medium" />}
						disabled={disabledAddButton}
						onClick={() => onAddNewInput(customFieldItemIndex)}
					/>
				</Stack>
			</Grid>
		</Grid>
	);
}
